// src/components/dashboard/OverallEmissionsProjection.js
import React, { useMemo } from "react";
import { StackedBarChart } from "./charts/StackedBarChart";
import { getSectorColor } from "../../config/colorScheme";
import { useSectorEmissionsWithFood } from "../../hooks/useSectorEmissionsWithFood";
// import { getSectorLabel, getSectorColour } from "../../lib/sectorMeta";

// Preferred legend/order for the overall emissions view (includes FOOD)
// const DEFAULT_SECTOR_ORDER = [
// //   "ELC",  // Power Generation
//   "IND",  // Industry
//   "TRA",  // Transport
// //   "AGR",  // Agriculture
//   "RES",  // Buildings & Homes
// //   "SER",  // Services / Commercial
// //   "WST",  // Waste
// //   "HYG",  // Hydrogen
// //   "CDR",  // Carbon removal
//   "FOOD", // Food (from FOOD_GHG_TOTAL)
// //   "OTH",  // Other
// ];
export const SECTOR_LABELS = {
  AGR: "Agriculture",
  IND: "Industry",
  RES: "Buildings",
  SER: "Buildings",
  COM: "Commercial",
  TRA: "Transport",
  BUILDINGS: "Buildings",
  ELC: "Power Generation",
  HYG: "Hydrogen",
  WST: "Waste",
  CDR: "Carbon removal",
  FOOD: "Food"
};
const getSectorLabel = (code) => SECTOR_LABELS[code] ?? String(code);

const DEFAULT_SECTOR_ORDER = ["FOOD", "BUILDINGS","TRA",  "IND"];
export function OverallEmissionsProjection({
  scenario,
  from = 2025,
  to = 2050,
  unitScale = 1,          // multiply values if you need (e.g., 1 for MtCO2e)
  sectors,                // optional: array of sector codes to include (in desired order)
  title = "GHG emissions",
  className = "w-full h-96 mt-4",
}) {
  const { labels, byGroupYear, groups, loading, error } =
    useSectorEmissionsWithFood({ scenario, from, to, valueDivisor: 1000});

  // Keep only requested sectors (or our default order), but also ensure they exist in the data.
  const activeCodes = useMemo(() => {
    const present = new Set(groups);
    const order = sectors && sectors.length ? sectors : DEFAULT_SECTOR_ORDER;
    return order.filter((code) => present.has(code));
  }, [groups, sectors]);

  const datasets = useMemo(() => {
    return activeCodes.map((code) => {
      const label = getSectorLabel(code);
      const color = getSectorColor(code,"primary");
              // make sure FOOD has a colour in sectorMeta
      const fill  = `${color}99`;                   // ~20% opacity
      return {
        label,
        data: labels.map((y) => (byGroupYear[code]?.[Number(y)] ?? 0) * unitScale),
        backgroundColor: fill,
        borderColor: color,
        borderWidth: 1,
        stack: "emis",
      };
    });
  }, [activeCodes, labels, byGroupYear, unitScale]);

  if (loading) return <div className="w-full h-96 grid place-items-center">Loading projections…</div>;
  if (error)   return <div className="w-full h-96 text-red-600">Error: {error.message}</div>;
  if (!labels.length) return <div className="w-full h-40 grid place-items-center text-sm text-slate-500">No data</div>;

  return (
    <StackedBarChart
      title={title}
      labels={labels}
      yAxisTitle="MtCO2e"
      datasets={datasets}
      className={className}
    />
  );
}

export default OverallEmissionsProjection;
